import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  AppBar,
  Toolbar,
  InputAdornment,
  CircularProgress,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';

const Categorias = () => {
  const [categorias, setCategorias] = useState([]);
  const [originalCategorias, setOriginalCategorias] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedCategoria, setSelectedCategoria] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    nombre_categoria: '',
    id_pet: '',
    habilitada: true,
  });
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userIdPet, setUserIdPet] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const navigate = useNavigate();

  // Decodificar token JWT para obtener id_pet
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        console.log('Payload del token:', payload);
        setUserIdPet(payload.id_pet || null);
      } catch (err) {
        console.error('Error al decodificar el token:', err);
        setError('Error al decodificar el token de usuario');
        setSnackbarMessage('Error al decodificar el token de usuario');
        setSnackbarOpen(true);
      }
    } else {
      setError('No se encontró un token de usuario');
      setSnackbarMessage('No se encontró un token de usuario');
      setSnackbarOpen(true);
    }
  }, []);

  // Formatear fechas
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Normalizar datos de categoría
  const normalizeCategoria = (categoria) => {
    if (!categoria || !categoria.id_categoria) {
      console.warn('Categoría inválida recibida:', categoria);
      return null;
    }
    return {
      id_categoria: categoria.id_categoria ?? null,
      nombre_categoria: categoria.nombre_categoria ?? '',
      id_pet: categoria.id_pet ?? null,
      habilitada: categoria.habilitada ?? true,
      createdAt: categoria.createdAt ?? null,
      updatedAt: categoria.updatedAt ?? null,
    };
  };

  // Obtener categorías y pets
  useEffect(() => {
    const fetchData = async () => {
      if (!userIdPet) {
        console.log('Esperando id_pet del usuario...');
        return;
      }
      try {
        setLoading(true);
        const categoriasRes = await api.get(`/categorias/?id_pet=${userIdPet}`);
        console.log('Respuesta de categorías:', categoriasRes.data);
        const categoriasData = Array.isArray(categoriasRes.data)
          ? categoriasRes.data.filter(item => item !== null && item !== undefined)
          : [];
        const normalizedCategorias = categoriasData
          .map(normalizeCategoria)
          .filter(cat => cat !== null && cat.id_categoria && cat.id_pet === userIdPet);
        console.log('Categorías normalizadas:', normalizedCategorias);
        setCategorias(normalizedCategorias);
        setOriginalCategorias(normalizedCategorias);
        const petsRes = await api.get('/pets/');
        console.log('Respuesta de pets:', petsRes.data);
        const petsData = Array.isArray(petsRes.data) ? petsRes.data : [];
        setPets(petsData);
        setLoading(false);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        const errorMsg = 'Error al cargar datos: ' + (error.response?.data?.detail || error.message);
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
        setLoading(false);
      }
    };
    fetchData();
  }, [userIdPet]);

  // Buscar categorías
  useEffect(() => {
    const searchCategorias = () => {
      console.log('Buscando con término:', searchTerm);
      console.log('Categorías originales:', originalCategorias);
      if (!searchTerm) {
        setCategorias(originalCategorias);
        console.log('Restaurando categorías:', originalCategorias);
        return;
      }
      const filteredCategorias = originalCategorias
        .filter(cat => cat && cat.id_categoria && cat.nombre_categoria)
        .filter(cat => cat.nombre_categoria.toLowerCase().includes(searchTerm.toLowerCase()));
      console.log('Categorías filtradas:', filteredCategorias);
      setCategorias(filteredCategorias);
      setPage(0);
    };
    searchCategorias();
  }, [searchTerm, originalCategorias]);

  // Abrir modal para crear/editar categoría
  const handleOpenModal = (categoria = null) => {
    setSelectedCategoria(categoria);
    setFormData(
      categoria
        ? normalizeCategoria(categoria) || { nombre_categoria: '', id_pet: userIdPet || '', habilitada: true }
        : { nombre_categoria: '', id_pet: userIdPet || '', habilitada: true }
    );
    setOpenModal(true);
  };

  // Cerrar Snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage('');
  };

  // Guardar categoría (crear o editar)
  const handleSave = async () => {
    try {
      if (!formData.nombre_categoria || !formData.id_pet) {
        const errorMsg = 'Nombre de la categoría y veterinaria son obligatorios';
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
        return;
      }
      if (selectedCategoria) {
        const response = await api.put(`/categorias/${selectedCategoria.id_categoria}`, {
          nombre_categoria: formData.nombre_categoria,
          id_pet: formData.id_pet,
          habilitada: formData.habilitada,
        });
        const updatedCategoria = normalizeCategoria(response.data);
        if (updatedCategoria) {
          const newCategorias = categorias.map(cat =>
            cat.id_categoria === selectedCategoria.id_categoria ? updatedCategoria : cat
          );
          setCategorias(newCategorias);
          setOriginalCategorias(newCategorias);
          setSnackbarMessage('Categoría actualizada exitosamente');
          setSnackbarOpen(true);
        }
      } else {
        const response = await api.post('/categorias/', {
          nombre_categoria: formData.nombre_categoria,
          id_pet: formData.id_pet,
          habilitada: formData.habilitada,
        });
        const newCategoria = normalizeCategoria(response.data);
        if (newCategoria) {
          const newCategorias = [...categorias, newCategoria];
          setCategorias(newCategorias);
          setOriginalCategorias(newCategorias);
          setSnackbarMessage('Categoría creada exitosamente');
          setSnackbarOpen(true);
        }
      }
      setOpenModal(false);
      setError(null);
      setPage(0);
    } catch (error) {
      console.error('Error al guardar categoría:', error);
      const errorMsg = 'Error al guardar categoría: ' + (error.response?.data?.detail || error.message);
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  };

  // Deshabilitar categoría
  const handleToggleHabilitada = async (id, habilitada) => {
    try {
      const response = await api.put(`/categorias/${id}`, { habilitada: !habilitada });
      const updatedCategoria = normalizeCategoria(response.data);
      if (updatedCategoria) {
        const newCategorias = categorias.map(cat =>
          cat.id_categoria === id ? updatedCategoria : cat
        );
        setCategorias(newCategorias);
        setOriginalCategorias(newCategorias);
        setSnackbarMessage(`Categoría ${updatedCategoria.habilitada ? 'habilitada' : 'deshabilitada'} exitosamente`);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error al actualizar estado:', error);
      const errorMsg = 'Error al actualizar estado: ' + (error.response?.data?.detail || error.message);
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  };

  // Eliminar categoría
  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta categoría?')) return;
    try {
      await api.delete(`/categorias/${id}`);
      const newCategorias = categorias.filter(cat => cat.id_categoria !== id);
      setCategorias(newCategorias);
      setOriginalCategorias(newCategorias);
      setSnackbarMessage('Categoría eliminada exitosamente');
      setSnackbarOpen(true);
      setPage(0);
    } catch (error) {
      console.error('Error al eliminar categoría:', error);
      const errorMsg = 'Error al eliminar categoría: ' + (error.response?.data?.detail || error.message);
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  };

  // Manejo de paginación
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const paginatedCategorias = categorias.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 3 }}>
      <AppBar position="relative" sx={{ bgcolor: '#1e293b', mb: 3 }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Gestión de Categorías
          </Typography>
          <TextField
            variant="outlined"
            placeholder="Buscar categorías..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ bgcolor: 'white', borderRadius: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>
      </AppBar>
      <Container maxWidth={false} disableGutters>
        {loading || !userIdPet ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center">{error}</Typography>
        ) : categorias.length > 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenModal()}
                >
                  Nueva Categoría
                </Button>
              </Box>
              <TableContainer
                component={Paper}
                sx={{
                  width: '100%',
                  overflowX: 'auto',
                  boxShadow: 3,
                }}
              >
                <Table
                  sx={{
                    tableLayout: 'auto',
                  }}
                  aria-label="tabla de categorías"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Nombre
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Veterinaria
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Habilitada
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Creado
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Actualizado
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Acciones
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedCategorias.map((row) => {
                      if (!row || !row.id_categoria) {
                        console.warn('Fila inválida en la tabla:', row);
                        return null;
                      }
                      return (
                        <TableRow
                          key={row.id_categoria}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {row.id_categoria}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {row.nombre_categoria}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {pets.find(p => p.id_pet === row.id_pet)?.nombre_veterinaria || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            <Switch
                              checked={row.habilitada === true || row.habilitada === false ? row.habilitada : false}
                              onChange={() => handleToggleHabilitada(row.id_categoria, row.habilitada ?? false)}
                              color="primary"
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {formatDate(row.createdAt)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {formatDate(row.updatedAt)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            <IconButton onClick={() => handleOpenModal(row)} color="primary">
                              <EditIcon />
                            </IconButton>
                            <IconButton onClick={() => handleDelete(row.id_categoria)} color="error">
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 2 }}>
                <Button
                  variant="contained"
                  disabled={page === 0}
                  onClick={() => handleChangePage(page - 1)}
                >
                  Anterior
                </Button>
                <Typography sx={{ alignSelf: 'center' }}>
                  Página {page + 1} de {Math.ceil(categorias.length / rowsPerPage)}
                </Typography>
                <Button
                  variant="contained"
                  disabled={page >= Math.ceil(categorias.length / rowsPerPage) - 1}
                  onClick={() => handleChangePage(page + 1)}
                >
                  Siguiente
                </Button>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6">Sin datos</Typography>
            <Typography color="text.secondary">
              No se encontraron categorías. Crea una nueva categoría para comenzar.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenModal()}
              sx={{ mt: 2 }}
            >
              Nueva Categoría
            </Button>
          </Box>
        )}
        <Dialog open={openModal} onClose={() => setOpenModal(false)}>
          <DialogTitle>{selectedCategoria ? 'Editar Categoría' : 'Nueva Categoría'}</DialogTitle>
          <DialogContent>
            <TextField
              label="Nombre de la Categoría"
              fullWidth
              value={formData.nombre_categoria}
              onChange={(e) => setFormData({ ...formData, nombre_categoria: e.target.value })}
              sx={{ mt: 2 }}
              required
            />
            <TextField
              select
              label="Veterinaria"
              fullWidth
              value={formData.id_pet}
              onChange={(e) => setFormData({ ...formData, id_pet: e.target.value })}
              sx={{ mt: 2 }}
              required
              disabled
            >
              {pets.length > 0 ? (
                pets.map((pet) => (
                  <MenuItem key={pet.id_pet} value={pet.id_pet}>
                    {pet.nombre_veterinaria}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No hay veterinarias disponibles
                </MenuItem>
              )}
            </TextField>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
              <Switch
                checked={formData.habilitada}
                onChange={(e) => setFormData({ ...formData, habilitada: e.target.checked })}
                color="primary"
              />
              <Typography>Habilitada</Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
            <Button onClick={handleSave} variant="contained" disabled={!formData.nombre_categoria || !formData.id_pet}>
              Guardar
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ zIndex: 1500 }}
        >
          <Alert onClose={handleSnackbarClose} severity={error ? 'error' : 'success'} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Categorias;